/**
 * ContactAutocomplete — drop-in `<input>` replacement that adds
 * autocomplete suggestions from the workspace's Contacts/Clients.
 *
 * Manual entry stays 100% intact: every keystroke flows through
 * `onChange(value)` exactly like a native input. Picking a suggestion
 * additionally calls `onSelect(contact)` so the parent form can autofill
 * sibling fields (first/last/phone/etc.) — never destructive.
 *
 * Workspace scoping is enforced by the backend (/api/contacts requires
 * workspace_access). The component only fetches once per mount; if no
 * workspaceId is passed it degrades silently to a plain input.
 *
 * Used by:
 *   - BookingsHub.jsx (CreateBookingDialog + EditBookingDialog)
 *   - CreateMeeting.jsx (invite-attendees input, append-on-pick mode)
 *   - MeetingRoom.jsx (in-meeting InviteModal, chip-on-pick mode)
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { User2, Users } from "lucide-react";
import api from "@/lib/api";

export default function ContactAutocomplete({
  workspaceId,
  value,
  onChange,
  onSelect,
  placeholder = "",
  type = "text",
  multiline = false,
  className = "bloq-input",
  wrapperClassName = "",
  id,
  dark = false,
  // When set, suggestion matching ignores the `value` (used by chip/CSV
  // inputs where `value` is a comma-soup of past entries). Caller may
  // also pass a custom `queryOverride` string to drive matching from
  // a separate draft field.
  queryOverride = null,
  // Custom data-testid for the underlying <input>. Suggestions get their
  // own testids derived from the contact_id.
  testid,
  // Optional: extra onBlur passthrough so callers (chip input) can still
  // commit drafts on blur. We add our own debounced close, then call
  // whatever the caller passed.
  onBlur,
  onKeyDown,
  ...rest
}) {
  const [contacts, setContacts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef(null);
  const closeTimerRef = useRef(null);
  const inputRef = useRef(null);

  // Multiline mode: grow the textarea to fit its content (capped) so wrapped
  // recipients stay visible instead of scrolling off the right edge.
  const autoGrow = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 220)}px`;
  };
  useEffect(() => {
    if (multiline) autoGrow(inputRef.current);
  }, [value, multiline]);

  // Lazy fetch on first focus, only once per mount.
  const ensureLoaded = async () => {
    if (loaded || !workspaceId) return;
    try {
      const r = await api.get("/contacts", { params: { workspace_id: workspaceId } });
      setContacts(Array.isArray(r.data) ? r.data : []);
    } catch {
      setContacts([]);
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  const query = (queryOverride != null ? queryOverride : value || "").toString().trim().toLowerCase();
  const suggestions = useMemo(() => {
    if (!query || !contacts.length) return [];
    return contacts
      .filter((c) => {
        const blob = [c.name, c.email, c.company, c.first_name, c.last_name, c.phone]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return blob.includes(query);
      })
      .slice(0, 6);
  }, [query, contacts]);

  // Always show suggestions when open + we have any (so the user sees
  // the list as soon as a known query matches).
  const showList = open && suggestions.length > 0;

  const pick = (c) => {
    clearTimeout(closeTimerRef.current);
    setOpen(false);
    onSelect?.(c);
  };

  const handleKeyDown = (e) => {
    if (showList) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlight((h) => Math.max(h - 1, 0));
        return;
      }
      if (e.key === "Enter" && suggestions[highlight]) {
        // Only intercept Enter if the caller hasn't already set up a
        // custom Enter handler (e.g. chip-commit). When a custom
        // onKeyDown is supplied we let it run AFTER picking so chip
        // logic still works for free-typed addresses.
        e.preventDefault();
        pick(suggestions[highlight]);
        return;
      }
      if (e.key === "Escape") {
        setOpen(false);
        return;
      }
    }
    onKeyDown?.(e);
  };

  const handleBlur = (e) => {
    // Defer close so a click inside the dropdown registers before the
    // input loses focus. Without this, mousedown on a suggestion races
    // with blur and the click is dropped.
    closeTimerRef.current = setTimeout(() => setOpen(false), 150);
    onBlur?.(e);
  };

  // Theme tokens — light by default, dark for the in-meeting modal.
  const listBg = dark ? "bg-[#1a1a1f] border-white/15" : "bg-white border-[#e5e0d6]";
  const itemHover = dark ? "hover:bg-white/10" : "hover:bg-[#f5efe6]";
  const itemActive = dark ? "bg-white/10" : "bg-[#f5efe6]";
  const itemText = dark ? "text-white" : "text-[#1a1a1f]";
  const itemMeta = dark ? "text-white/60" : "text-[#82828c]";
  const pillBase = "ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-semibold flex-shrink-0";

  return (
    <div ref={wrapperRef} className={`relative ${wrapperClassName}`}>
      {multiline ? (
        <textarea
          ref={inputRef}
          id={id}
          rows={2}
          className={className}
          value={value ?? ""}
          onChange={(e) => { onChange?.(e.target.value); setOpen(true); autoGrow(e.target); }}
          onFocus={() => { ensureLoaded(); setOpen(true); setHighlight(0); }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          data-testid={testid}
          style={{ resize: "none", overflow: "hidden" }}
          {...rest}
        />
      ) : (
        <input
          id={id}
          type={type}
          className={className}
          value={value ?? ""}
          onChange={(e) => { onChange?.(e.target.value); setOpen(true); }}
          onFocus={() => { ensureLoaded(); setOpen(true); setHighlight(0); }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          data-testid={testid}
          {...rest}
        />
      )}
      {showList && (
        <ul
          className={`absolute z-50 left-0 right-0 mt-1 max-h-72 overflow-y-auto border shadow-lg ${listBg}`}
          role="listbox"
          data-testid={testid ? `${testid}-suggestions` : "contact-suggestions"}
          onMouseDown={(e) => e.preventDefault()}
        >
          {suggestions.map((c, idx) => {
            const isClient = !!c.is_client;
            return (
              <li
                key={c.contact_id || `${c.email}-${idx}`}
                className={`px-3 py-2 cursor-pointer flex items-center gap-2 ${itemHover} ${idx === highlight ? itemActive : ""}`}
                onMouseEnter={() => setHighlight(idx)}
                onClick={() => pick(c)}
                role="option"
                aria-selected={idx === highlight}
                data-testid={`contact-suggestion-${c.contact_id || idx}`}
              >
                <div className="min-w-0 flex-1">
                  <div className={`text-sm font-medium truncate ${itemText} flex items-center`}>
                    <span className="truncate">
                      {c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email || "(unnamed)"}
                    </span>
                    <span
                      className={pillBase}
                      style={
                        isClient
                          ? { background: "rgba(0,94,122,0.18)", color: dark ? "#7dd3fc" : "#005e7a" }
                          : { background: dark ? "rgba(255,255,255,0.1)" : "rgba(130,130,140,0.18)", color: dark ? "#cbd5e1" : "#4a4a52" }
                      }
                    >
                      {isClient ? <Users className="w-2.5 h-2.5" /> : <User2 className="w-2.5 h-2.5" />}
                      {isClient ? "Client" : "Contact"}
                    </span>
                  </div>
                  <div className={`text-xs truncate ${itemMeta}`}>
                    {c.email || "no email"}{c.company ? ` · ${c.company}` : ""}{c.phone ? ` · ${c.phone}` : ""}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
